import {Course} from '@jobseeker/common-core/types';
import {
  DefaultJobLogo,
  HiristBriefcase,
  HiristClock,
  HiristOpenBook,
  HiristRupee,
} from '@jobseeker/lotus/assets/icons';
import {HiristBanner} from '@jobseeker/lotus/components';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {
  Box,
  styled,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';
import {getRandomColor} from '@jobseeker/lotus/theme';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  DownloadBrochureBtn,
  EnquireNowBtn,
  EnquiryType,
} from './EnquiryButtons';

type HeaderProps = {
  courseInfo: Course;
  buttonLoading?: boolean;
  courseEnquired?: boolean;
  handleEnquiryButtonClick?: (type: EnquiryType) => void;
  forUnpublishedCourse?: boolean;
};

const Heading: React.FC<TypographyProps> = styled(Typography)(({theme}) => ({
  fontWeight: 'bold',
  fontSize: theme.typography.pxToRem(16),
}));

const DefaultLogo = ({
  courseId = 0,
  isDeviceLarge = true,
}: {
  courseId?: number;
  isDeviceLarge?: boolean;
}) => {
  const theme = useTheme();
  const randomColor = getRandomColor(courseId);
  return (
    <Box
      className="joblist__logo"
      sx={{
        background: randomColor,
        p: theme.spacing(1.5),
        lineHeight: 0,
        borderRadius: '10px',
        alignSelf: isDeviceLarge ? 'center' : 'start',
        width: 'fit-content',
        height: 'fit-content',
      }}
    >
      <Image
        src={DefaultJobLogo}
        alt={'Logo'}
        height={isDeviceLarge ? 76 : 40}
        width={isDeviceLarge ? 76 : 40}
        objectFit="cover"
        quality={50}
      />
    </Box>
  );
};

const Header = (props: HeaderProps) => {
  const {
    courseInfo: {
      fee,
      path,
      title,
      brochure = '',
      duration,
      oneLiner,
      introtext,
      maxWorkExp,
      minWorkExp,
      deliveryMethod,
      alreadyEnquired,
      courseHighlight,
      desiredCandProfile,
    },
    courseEnquired = false,
    buttonLoading = false,
    handleEnquiryButtonClick,
    forUnpublishedCourse = false,
  } = props;

  const experience = `${minWorkExp} - ${maxWorkExp} Years`;

  const isDeviceLarge = useIsLargeDevice('md');

  const theme = useTheme();

  return (
    <Box mt={2.875}>
      <HiristBanner>
        {path.length > 0 ? (
          <Image
            src={path}
            alt="company-icon"
            height={isDeviceLarge ? 100 : 50}
            width={isDeviceLarge ? 100 : 50}
            style={{
              alignSelf: 'start',
              borderRadius: '8px',
              backgroundColor: 'white',
              marginTop: isDeviceLarge ? '8px' : '4px',
            }}
          />
        ) : (
          <DefaultLogo isDeviceLarge={isDeviceLarge} />
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          mb={{xxs: 5, md: 0}}
        >
          <Box display="flex" flexDirection="column" ml={4}>
            <Typography
              component={'h1'}
              fontSize={isDeviceLarge ? 24 : 16}
              fontWeight={700}
              color="white"
              mb={1}
            >
              {title}
            </Typography>
            {isDeviceLarge && (
              <Box color="white">
                <Typography component="span" fontSize={isDeviceLarge ? 16 : 14}>
                  {oneLiner}
                </Typography>
              </Box>
            )}
            <Box
              color="white"
              display="flex"
              gap={{xxs: 0, md: 4}}
              rowGap={{xxs: 2}}
              flexWrap="wrap"
              mt={{xxs: 0, md: 2.5}}
            >
              <Box
                display="flex"
                gap={2}
                sx={isDeviceLarge ? {} : {maxWidth: 140, width: '100%'}}
              >
                <Image
                  src={HiristRupee}
                  height={13}
                  width={13}
                  alt="course-fee"
                  style={{marginTop: 5}}
                />
                <Box>
                  <Typography fontSize={14} fontWeight={300}>
                    Fee
                  </Typography>
                  <Typography fontWeight={700} fontSize={14}>
                    {fee}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={2} maxWidth={140}>
                <Image
                  src={HiristBriefcase}
                  height={13}
                  width={13}
                  alt="course-experience"
                  style={{marginTop: 5}}
                />
                <Box>
                  <Typography fontSize={14} fontWeight={300}>
                    Experience
                  </Typography>
                  <Typography fontWeight={700} fontSize={14}>
                    {experience}
                  </Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                gap={2}
                sx={isDeviceLarge ? {} : {maxWidth: 140, width: '100%'}}
              >
                <Image
                  src={HiristClock}
                  height={13}
                  width={13}
                  alt="course-duration"
                  style={{marginTop: 5}}
                />
                <Box>
                  <Typography fontSize={14} fontWeight={300}>
                    Duration
                  </Typography>
                  <Typography fontWeight={700} fontSize={14}>
                    {duration}
                  </Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                gap={2}
                sx={isDeviceLarge ? {} : {maxWidth: 120}}
              >
                <Image
                  src={HiristOpenBook}
                  height={16}
                  width={16}
                  alt="course-delivery-method"
                  style={{marginTop: 4}}
                />
                <Box>
                  <Typography fontSize={14} fontWeight={300}>
                    Delivery Method
                  </Typography>
                  <Typography fontWeight={700} fontSize={14}>
                    {deliveryMethod}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          {isDeviceLarge && !forUnpublishedCourse && (
            <Box>
              <EnquireNowBtn
                text={
                  Boolean(alreadyEnquired) || courseEnquired
                    ? 'Enquired'
                    : 'Enquire Now'
                }
                sx={{
                  bgcolor: `${theme.palette.primary.main} !important`,
                  color: `${theme.palette.primary.contrastText} !important`,
                  borderRadius: '4px',
                  height: 48,
                  width: 220,
                  display: 'block',
                  fontSize: theme.typography.pxToRem(16),
                  fontWeight: 400,
                  mb: 2,
                  mt: 1,
                }}
                disabled={Boolean(alreadyEnquired) || courseEnquired}
                buttonLoading={buttonLoading}
                handleEnquiryButtonClick={handleEnquiryButtonClick}
              />
              {brochure.length > 0 && (
                <DownloadBrochureBtn
                  sx={{
                    bgcolor: `${theme.palette.primary.contrastText} !important`,
                    color: `${theme.palette.text.primary} !important`,
                    fontSize: theme.typography.pxToRem(16),
                    fontWeight: 400,
                    borderRadius: '4px',
                    height: 48,
                    border: 'none',
                  }}
                  handleEnquiryButtonClick={handleEnquiryButtonClick}
                />
              )}
              <Box mt={1} textAlign="center">
                <Typography
                  fontSize={theme.typography.pxToRem(10)}
                  fontWeight={300}
                  lineHeight="12px"
                  color={theme.palette.primary.contrastText}
                >
                  You may be contacted by the course provider
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </HiristBanner>
      {!isDeviceLarge && (
        <Box
          bgcolor={theme.palette.primary.contrastText}
          mt={2.25}
          py={2.5}
          px={2.5}
          borderRadius="16px"
          position="relative"
          top={-45}
          sx={{
            borderRadius: {xs: '15px', md: '8px'},
            '& p': {
              margin: '7px 0px',
              fontSize: 16,
              textAlign: 'justify',
              lineHeight: '26px',
              color: '#000',
            },
            '& b': {
              color: '#000',
              textAlign: 'justify',
              fontSize: 18,
              fontWeight: 'bold',
              display: 'inline-block',
            },
            '& ul': {
              listStyle: 'disc',
              padding: '0px 25px',
            },
            '& li': {
              fontSize: 16,
              lineHeight: '20px',
              marginRight: 0.5,
            },
          }}
        >
          <Box mt={0}>
            <div
              dangerouslySetInnerHTML={{__html: introtext}}
              style={{
                fontSize: '14px',
              }}
            />
          </Box>

          <Box mt={4}>
            <Heading component="h2">Desired Candidate Profile</Heading>
            <div
              dangerouslySetInnerHTML={{__html: desiredCandProfile}}
              style={{
                fontSize: '14px',
              }}
            />
          </Box>

          <Box mt={4}>
            <Heading component="h2">Course Modules</Heading>
            <div
              dangerouslySetInnerHTML={{__html: courseHighlight}}
              style={{
                fontSize: '14px',
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Header;
